import { useState } from "react";
import { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { usePatientPdfs } from "CustomHooks/usePatientPdfs";
import { usePatientContext } from "Context/Patient";
import { tabIndexes } from "Constants/Patient";
import Table from "Components/Common/Table/Table";
import {
  VisitPdfsTableHeading,
  VisitPdfsTableBody,
  VISIT_PDFS_COLUMN_COUNT,
} from "Components/Patients/EditViewPatient/Forms/VisitTable/VisitPdfsTable";
import { DoctorNoteModal } from "Components/Patients/EditViewPatient/Forms/VisitTable/DoctorNoteModal";
import { Spinner } from "Components/Common/LoadingSpinner/Spinner";
import { PaginationBtns } from "Components/Common/PaginationBtns/PaginationBtns";
import { ErrorPopUp } from "Components/Common/PopUp/PopUp";
import { errorFormatter } from "Helpers/ErrorMsgHandler";
import { Button } from "Components/Common/Button/Button";
import "Colors.css";

const ContentModal = ({ open, content, onClose }) => (
  <Transition.Root show={open} as={Fragment}>
    <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" onClose={onClose}>
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enterTo="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 translate-y-0 sm:scale-100"
          leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <div className="inline-block align-bottom bg-white rounded-lg px-6 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
            <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Note Content
            </Dialog.Title>
            <div className="mt-2 max-h-96 overflow-y-auto">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{content}</p>
            </div>
            <div className="mt-5">
              <Button
                variant="transparent"
                className="text-sm font-medium sapphire bg-transparent border border-gray-200 py-2 px-4 inline-flex rounded-md"
                btnText="Close"
                isDisabled={false}
                onClick={onClose}
              />
            </div>
          </div>
        </Transition.Child>
      </div>
    </Dialog>
  </Transition.Root>
);

export const DoctorNotesFormView = ({ setActiveTabIndex }) => {
  const { getPatientData } = usePatientContext();
  const [apiError, setApiError] = useState(false);
  const [notice, setNotice] = useState(false);
  const [contentModal, setContentModal] = useState({ open: false, content: "" });
  const [noteModal, setNoteModal] = useState({ open: false, note: null });

  const patientData = getPatientData();
  const currentPatientId = patientData?.id || "";
  // Rows span every visit, so each row invalidates against its own visitId
  // (VisitPdfsTableBody prefers pdf.visitId and falls back to this prop).
  const currentVisitId = "";

  const { pdfs, isLoading, isError, error, hasNext, hasPrevious, goToNextPage, goToPreviousPage } =
    // Pagination controls were removed from this tab — one big page keeps
    // every note reachable.
    usePatientPdfs(currentPatientId, 1, 200);

  const displayError = apiError || (isError ? errorFormatter(error?.message) : false);

  const handleViewPdfError = (message) => {
    setApiError(message);
  };

  const handleInvalidateError = (message) => {
    setApiError(message);
  };

  const handleCopied = (message) => {
    setNotice(message);
  };

  const handleViewContent = (content) => {
    setContentModal({ open: true, content });
  };

  return (
    <>
      {displayError && <ErrorPopUp error={displayError} setApiError={setApiError} />}
      {notice && <ErrorPopUp msg={notice} setApiError={setNotice} />}

      <div className="flex justify-end px-2 pb-2">
        <Button
          btnText="Add Note"
          isDisabled={!currentPatientId}
          onClick={() => setNoteModal({ open: true, note: null })}
          className="text-sm font-medium text-white bg-blue-chill py-2 px-4 inline-flex rounded-md"
        />
      </div>

      {/* 15 columns will not fit the detail pane. Scroll the table inside its
          own container rather than letting it widen the whole page, which
          would push the tab nav off-screen. Wrapped here rather than in the
          shared Table so the per-visit screen keeps its current behaviour. */}
      <div className="overflow-x-auto w-full" style={{ padding: "0.5rem" }}>
      <Table
        heading={<VisitPdfsTableHeading />}
        body={
          !isLoading && pdfs.length > 0 ? (
            <VisitPdfsTableBody
              data={pdfs}
              visitId={currentVisitId}
              patientId={currentPatientId}
              onViewContent={handleViewContent}
              onViewPdfError={handleViewPdfError}
              onInvalidateError={handleInvalidateError}
              onCopied={handleCopied}
              onEdit={(note) => setNoteModal({ open: true, note })}
            />
          ) : (
            !isLoading && (
              <tr>
                <td colSpan={VISIT_PDFS_COLUMN_COUNT} className="text-center py-10 text-gray-400 bg-white">
                  None
                </td>
              </tr>
            )
          )
        }
      />

      </div>

      {isLoading && <Spinner />}


      <ContentModal open={contentModal.open} content={contentModal.content} onClose={() => setContentModal({ open: false, content: "" })} />

      <DoctorNoteModal
        open={noteModal.open}
        note={noteModal.note}
        patient={patientData}
        visitId={noteModal.note?.visitId ? String(noteModal.note.visitId) : ""}
        onClose={() => setNoteModal({ open: false, note: null })}
        onSuccess={(message) => setNotice(message)}
      />
    </>
  );
};
